
import { useState } from 'react';
import { Loader2, PlusCircle, Map, LayoutList } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { Ride, Point } from '@/lib/types/core';
import RideCard from '@/components/ui-custom/RideCard';
import RideMap from '@/components/map/RideMap';
import RideListView from '@/components/ui-custom/RideListView';

interface UpcomingRidesSectionProps {
  upcomingRides: Ride[];
  loadingRides: boolean;
  defaultCenter: Point | null;
}

const UpcomingRidesSection = ({ upcomingRides, loadingRides, defaultCenter }: UpcomingRidesSectionProps) => {
  const [mapViewMode, setMapViewMode] = useState<'map' | 'list'>('map');

  return (
    <section className="section-padding bg-adv-charcoal">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10">
          <div>
            <h2 className="heading-lg text-white mb-2">Explore Rides</h2>
            <p className="text-adv-lightGray">Find group rides or create your own adventure.</p>
          </div>
          <NavLink to="/explore-rides" className="btn-primary mt-4 md:mt-0">
            Explore Rides
          </NavLink>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div>
            <h3 className="heading-sm text-white mb-4">Upcoming Rides</h3>
            
            {loadingRides ? (
              <div className="flex justify-center py-8">
                <Loader2 size={24} className="text-adv-orange animate-spin" />
              </div>
            ) : upcomingRides.length > 0 ? (
              <div className="space-y-4">
                {upcomingRides.map(ride => (
                  <RideCard
                    key={ride.id}
                    id={ride.id}
                    title={ride.title}
                    date={ride.meeting_date}
                    location={ride.meeting_address || `${ride.meeting_longitude}, ${ride.meeting_latitude}`}
                    difficulty={ride.terrain_difficulty}
                    distance={ride.ride_type}
                    organizer={ride.created_by}
                    multi_day={ride.multi_day}
                    end_date={ride.end_date}
                  />
                ))}
              </div>
            ) : (
              <div className="glass-card p-6 text-center">
                <p className="text-adv-lightGray">No upcoming rides scheduled yet.</p>
                <NavLink to="/explore-rides" className="btn-primary mt-4 inline-flex items-center">
                  <PlusCircle size={16} className="mr-2" /> Create a Ride
                </NavLink>
              </div>
            )}
          </div>
          
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading-sm text-white">Ride Map</h3>
              <div className="flex space-x-2">
                <button
                  className={`p-2 rounded-md ${mapViewMode === 'map' ? 'bg-adv-orange text-white' : 'bg-adv-darkGray text-adv-lightGray hover:bg-adv-darkGray/70'}`}
                  onClick={() => setMapViewMode('map')}
                  aria-label="Map View"
                  title="Map View"
                >
                  <Map size={18} />
                </button>
                <button
                  className={`p-2 rounded-md ${mapViewMode === 'list' ? 'bg-adv-orange text-white' : 'bg-adv-darkGray text-adv-lightGray hover:bg-adv-darkGray/70'}`}
                  onClick={() => setMapViewMode('list')}
                  aria-label="List View"
                  title="List View"
                >
                  <LayoutList size={18} />
                </button>
              </div>
            </div>
            
            {mapViewMode === 'map' ? (
              <RideMap rides={upcomingRides} loading={loadingRides} defaultZoom={2} defaultCenter={defaultCenter || {
                longitude: -98.5795,
                latitude: 39.8283,
              }} />
            ) : (
              <RideListView rides={upcomingRides} loading={loadingRides} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UpcomingRidesSection;
